/* eslint-disable react/prop-types */
import FormatSize from '@mui/icons-material/FormatSize';
import {
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
} from '@mui/joy';
import {
  Check,
  LinkSimple,
  ListBullets,
  ListNumbers,
  TextAa,
  TextBolder,
  TextH,
  TextItalic,
  TextUnderline,
  LinkBreak,
  X,
} from '@phosphor-icons/react';
import { mergeProps } from '@react-aria/utils';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  ChainedCommands,
  Editor,
  EditorContent,
  useEditor,
  Node,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from './button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { useFormController } from './form';
import { inputFieldClasses } from './input';
import { Text } from './text';

import { TextStyleExtended } from '../common/editor/extensions/FontSize';

import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';

function createSafeContext<ContextValue>(errorMessage: string) {
  const Context = createContext<ContextValue | null>(null);

  const useSafeContext = () => {
    const ctx = useContext(Context);

    if (ctx === null) {
      throw new Error(errorMessage);
    }

    return ctx;
  };

  const Provider = ({
    children,
    value,
  }: {
    value: ContextValue;
    children: React.ReactNode;
  }) => <Context.Provider value={value}>{children}</Context.Provider>;

  return [Provider, useSafeContext] as const;
}

interface RichTextEditorContext {
  editor: Editor | null;
  editable?: boolean;
}

export const [RichTextEditorProvider, useRichTextEditorContext] =
  createSafeContext<RichTextEditorContext>(
    'RichTextEditor component was not found in tree',
  );

type CreateControlProps = {
  label: string;
  icon: React.FC;
  isActive?: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attributes?: Record<string, any> | string;
  };
  isDisabled?: (editor: Editor) => boolean;
  operation: {
    name: keyof ChainedCommands | ((editor: Editor) => void);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attributes?: Record<string, any> | string;
  };
};

function createControl({
  label,
  isActive,
  operation,
  icon,
  isDisabled,
  ...createForwardedProps
}: CreateControlProps & React.ComponentPropsWithoutRef<typeof IconButton>) {
  return forwardRef<
    HTMLButtonElement,
    React.ComponentPropsWithoutRef<typeof IconButton>
  >(function EditorControl(props, ref) {
    const { editor, editable } = useRichTextEditorContext();
    const Icon = icon;

    if (!editor) return null;

    const disabled = !editable || (isDisabled?.(editor) ?? false);
    const active = isActive?.name
      ? editor?.isActive(isActive.name, isActive.attributes)
      : false;
    return (
      <IconButton
        ref={ref}
        data-rich-text-editor-control
        {...mergeProps(createForwardedProps, props)}
        aria-label={label}
        title={label}
        disabled={disabled}
        aria-pressed={active}
        onClick={() => {
          if (typeof operation.name === 'function') {
            return operation.name(editor);
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          editor.chain().focus()[operation.name](operation.attributes).run();
        }}
      >
        <Icon />
      </IconButton>
    );
  });
}

const ControlsRoot = () => {
  return null;
};
const Controls = Object.assign(ControlsRoot, {
  Formatting: createControl({
    icon: TextAa,
    label: 'Clear Formatting',
    operation: {
      name: (editor) =>
        editor.chain().focus().selectAll().clearNodes().unsetAllMarks().run(),
    },
    variant: 'soft',
    color: 'primary',
  }),
  Bold: createControl({
    icon: TextBolder,
    isActive: { name: 'bold' },
    label: 'Bold',
    operation: { name: 'toggleBold' },
  }),
  Italic: createControl({
    isActive: { name: 'italic' },
    icon: TextItalic,
    label: 'Italic',
    operation: { name: 'toggleItalic' },
  }),
  Underline: createControl({
    isActive: { name: 'underline' },
    icon: TextUnderline,
    label: 'Underline',
    operation: { name: 'toggleUnderline' },
  }),
  UnorderedList: createControl({
    icon: ListBullets,
    label: 'Bulleted List',
    operation: { name: 'toggleBulletList' },
  }),
  OrderedList: createControl({
    icon: ListNumbers,
    label: 'Numbered List',
    operation: { name: 'toggleOrderedList' },
  }),
});
export const EditorControls = Controls;

//PRESERVES THE COMMENT SPAN WHEN THE EDITOR IS UPDATED
const CommentSpan = Node.create({
  name: 'commentSpan',
  group: 'inline',
  inline: true,
  content: 'text*',

  addAttributes() {
    return {
      class: {
        default: 'attached-comment',
      },
      'data-comment-id': {
        default: null,
      },
      style: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[class="attached-comment"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },
});

const LinkExtension = Link.configure({
  openOnClick: false,
  autolink: false,
  defaultProtocol: 'https',
  protocols: ['mailto', 'http', 'https'],
  isAllowedUri: (url, ctx) => {
    try {
      // construct URL
      const parsedUrl = url.includes(':')
        ? new URL(url)
        : new URL(`${ctx.defaultProtocol}://${url}`);

      // use default validation
      if (!ctx.defaultValidate(parsedUrl.href)) {
        return false;
      }

      // disallowed protocols
      const disallowedProtocols = ['ftp', 'file'];
      const protocol = parsedUrl.protocol.replace(':', '');

      if (disallowedProtocols.includes(protocol)) {
        return false;
      }

      // only allow protocols specified in ctx.protocols
      const allowedProtocols = ctx.protocols.map((p) =>
        typeof p === 'string' ? p : p.scheme,
      );

      if (!allowedProtocols.includes(protocol)) {
        return false;
      }

      // disallowed domains
      const disallowedDomains = [] as string[];
      const domain = parsedUrl.hostname;

      if (disallowedDomains.includes(domain)) {
        return false;
      }

      // all checks have passed
      return true;
    } catch {
      return false;
    }
  },
  shouldAutoLink: (url) => {
    try {
      // construct URL
      const parsedUrl = url.includes(':')
        ? new URL(url)
        : new URL(`https://${url}`);

      // only auto-link if the domain is not in the disallowed list
      const disallowedDomains = [] as string[];
      const domain = parsedUrl.hostname;

      return !disallowedDomains.includes(domain);
    } catch {
      return false;
    }
  },
}).extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: 'hyperlink',
      },
      style: {
        default: 'color: blue; text-decoration: underline;',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'a[href]',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['a', { ...HTMLAttributes }, 0];
  },
});

export function RichTextEditor({
  defaultValue = '',
  controlled = true,
  children,
}: {
  children: React.ReactNode;
  defaultValue?: string;
  controlled?: boolean;
}) {
  const ctx = useFormController();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyleExtended,
      CommentSpan,
      LinkExtension,
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-base focus:outline-none min-w-full',
      },
    },
    content: ctx?.field.value ?? defaultValue,
    editable: true,
    onUpdate(props) {
      if (ctx && controlled) {
        const hasValue = !!props.editor.getText();
        ctx.field.onChange(hasValue ? props.editor.getHTML() : '');
      }
    },
    onBlur: () => {
      if (ctx && controlled) {
        ctx.field.onBlur();
      }
    },
  });

  useEffect(() => {
    if (controlled && editor) {
      const cursorPosition = editor.state.selection;

      editor.commands.setContent(ctx?.field.value ?? '', false, {
        preserveWhitespace: 'full',
      });

      // This is to set the cursor position after setting the content to prevent the cursor from jumping to the end
      editor.commands.setTextSelection(cursorPosition);
    }
  }, [ctx?.field.value, controlled, editor]);

  return (
    <RichTextEditorProvider value={{ editor, editable: true }}>
      {children}
    </RichTextEditorProvider>
  );
}

export function RichTextEditorContent() {
  const { editor } = useRichTextEditorContext();
  if (!editor) {
    return null;
  }

  return <EditorContent editor={editor} />;
}

export function HeadingMenu() {
  const { editor } = useRichTextEditorContext();
  if (!editor) {
    return null;
  }

  return (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{
          root: {
            variant: 'plain',
            color: editor.isActive('heading') ? 'primary' : 'neutral',
          },
        }}
      >
        <TextH />
      </MenuButton>
      <Menu disablePortal>
        <MenuItem
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Heading 1' />
            </Text>
            {editor?.isActive('heading', {
              level: 1,
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Heading 2' />
            </Text>
            {editor?.isActive('heading', {
              level: 2,
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Heading 3' />
            </Text>
            {editor?.isActive('heading', {
              level: 3,
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 4 }).run()
          }
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Heading 4' />
            </Text>
            {editor?.isActive('heading', {
              level: 4,
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 5 }).run()
          }
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Heading 5' />
            </Text>
            {editor?.isActive('heading', {
              level: 5,
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 6 }).run()
          }
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Heading 6' />
            </Text>
            {editor?.isActive('heading', {
              level: 6,
            }) && <Check />}
          </div>
        </MenuItem>
      </Menu>
    </Dropdown>
  );
}

export function FontSizeMenu() {
  const { editor } = useRichTextEditorContext();
  if (!editor) {
    return null;
  }

  return (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{
          root: {
            variant: 'plain',
            color: editor.isActive('textStyle') ? 'primary' : 'neutral',
          },
        }}
      >
        <FormatSize />
      </MenuButton>
      <Menu disablePortal>
        <MenuItem
          onClick={() => editor.chain().focus().setFontSize('12').run()}
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Small' />
            </Text>
            {editor?.isActive('textStyle', {
              fontSize: '12',
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem onClick={() => editor.chain().focus().unsetFontSize().run()}>
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Normal' />
            </Text>
            {!editor?.isActive('textStyle') && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() => editor.chain().focus().setFontSize('18').run()}
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Medium' />
            </Text>
            {editor.isActive('textStyle', {
              fontSize: '18',
            }) && <Check />}
          </div>
        </MenuItem>
        <MenuItem
          onClick={() => editor.chain().focus().setFontSize('24').run()}
        >
          <div className='flex justify-between items-center gap-4'>
            <Text>
              <FormattedMessage defaultMessage='Large' />
            </Text>
            {editor?.isActive('textStyle', {
              fontSize: '24',
            }) && <Check />}
          </div>
        </MenuItem>
      </Menu>
    </Dropdown>
  );
}
export function HyperLink() {
  const intl = useIntl();
  const { editor } = useRichTextEditorContext();
  const { createToastNotification } = useToastNotificationContext();
  if (!editor) {
    return null;
  }

  const setLink = (url: string) => {
    if (url) {
      try {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: url })
          .run();
      } catch (e) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Invalid URL',
          }),
          'error',
        );
      }
    }
  };

  if (editor.state.selection.empty)
    return (
      <Tooltip
        title={intl.formatMessage({
          defaultMessage: 'Highlight text to HyperLink',
        })}
      >
        <IconButton variant='plain' color='neutral'>
          <LinkSimple />
        </IconButton>
      </Tooltip>
    );

  return (
    <div className='flex gap-2'>
      {editor.isActive('link') && (
        <Tooltip
          title={intl.formatMessage({
            defaultMessage: 'Remove Hyperlink',
          })}
        >
          <IconButton
            variant='plain'
            color='danger'
            onClick={editor.chain().focus().unsetLink().run}
          >
            <LinkBreak />
          </IconButton>
        </Tooltip>
      )}
      <AddHyperLinkModal onConfirm={setLink}>
        {(data) => (
          <Tooltip
            title={intl.formatMessage({
              defaultMessage: 'Add or Edit Hyperlink',
            })}
          >
            <IconButton
              variant='plain'
              color='primary'
              onClick={() => {
                const link = editor.getAttributes('link').href;
                if (link) editor.commands.unsetLink();
                data.setPrevUrl(link || '');
              }}
            >
              <LinkSimple />
            </IconButton>
          </Tooltip>
        )}
      </AddHyperLinkModal>
    </div>
  );
}

function AddHyperLinkModal({
  children,
  onConfirm,
}: {
  children: (data: { setPrevUrl: (url: string) => void }) => ReactNode;
  onConfirm: (url: string) => void;
}) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [hyperlinkUrl, setHyperlinkUrl] = useState('');
  const [prevHyperlinkUrl, setPrevHyperlinkUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onConfirmHandler = (url: string) => {
    if (url === '') {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'URL is required',
        }),
        'error',
      );
    } else {
      onConfirm(url);
      setIsModalOpen(false);
    }
  };

  const setPrevUrl = (url: string) => {
    setIsModalOpen(true);
    setHyperlinkUrl(url);
    if (url) setPrevHyperlinkUrl(url);
  };

  const onModalClose = (val: boolean) => {
    setIsModalOpen(val);
    if (!val) {
      onConfirm(prevHyperlinkUrl);
    }
  };

  return (
    <Dialog modal={true} open={isModalOpen} onOpenChange={onModalClose}>
      <DialogTrigger asChild>{children({ setPrevUrl })}</DialogTrigger>
      <DialogContent>
        <div className='flex justify-between items.-center'>
          <DialogTitle>
            <FormattedMessage defaultMessage='Enter Hyperlink Url' />
          </DialogTitle>
          <DialogClose asChild>
            <IconButton>
              <X />
            </IconButton>
          </DialogClose>
        </div>
        <div className='flex flex-col gap-2'>
          <input
            className={clsx(
              inputFieldClasses,
              'border-2 border-brand-primary-500 rounded-md p-2',
            )}
            value={hyperlinkUrl}
            onChange={(e) => {
              setHyperlinkUrl(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onConfirmHandler(hyperlinkUrl);
              }
            }}
          ></input>
          <div className='flex gap-1'>
            <Button
              fullWidth
              variant='solid'
              color='secondary'
              onClick={() => onConfirmHandler(hyperlinkUrl)}
            >
              <FormattedMessage defaultMessage='Confirm' />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
